import React, { useState } from 'react';
import { Button, Collapse, Modal, Tag } from 'antd';
import { DeleteOutlined, EditOutlined, FileWordOutlined } from '@ant-design/icons';
import type { Attachment } from '../../types';
import type { DockingAttachment, DockingRecord } from '../../types/docking';
import { inferAttachmentType } from '../../utils/parkAttachments';
import { AttachmentPreviewModal } from './AttachmentPreviewModal';
import { TaskAssignmentEditor } from './TaskAssignmentEditor';

const { Panel } = Collapse;

const PUBLIC_PREFIX = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');

function toPreviewAttachment(da: DockingAttachment): Attachment | null {
  if (!da.filePath) return null;
  const type = inferAttachmentType(da.fileName);
  if (!type) return null;
  const path = da.filePath.startsWith('http')
    ? da.filePath
    : `${PUBLIC_PREFIX}${da.filePath.startsWith('/') ? '' : '/'}${da.filePath}`;
  return { label: da.fileName, path: encodeURI(path), type };
}

interface DockingRecordCardProps {
  record: DockingRecord;
  onEdit: (record: DockingRecord) => void;
  onDelete: (id: string) => void;
}

function DetailRow(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="dash-detail-row">
      <div className="dash-detail-row__label">{props.label}</div>
      <div className="dash-detail-row__value">{props.children}</div>
    </div>
  );
}

function participantText(p: { name: string; title: string }): string {
  if (p.title) return `${p.name}（${p.title}）`;
  return p.name;
}

export function DockingRecordCard(props: DockingRecordCardProps): React.ReactElement {
  const { record, onEdit, onDelete } = props;
  const [preview, setPreview] = useState<Attachment | null>(null);

  const handleDelete = () => {
    Modal.confirm({
      title: '确认删除',
      content: '删除后将归档此对接记录，确定继续？',
      okText: '确认',
      cancelText: '取消',
      onOk: () => onDelete(record.id),
    });
  };

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
      <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
        {record.dockedAt}
      </span>
      <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, flex: 1 }}>
        {record.counterpartyOrg}
      </span>
      {record.blockers && !record.blockerResolved && (
        <Tag color="warning" style={{ marginRight: 0, fontSize: 11 }}>有卡点</Tag>
      )}
    </div>
  );

  return (
    <Collapse ghost className="docking-record-collapse" defaultActiveKey={[]}>
      <Panel
        key={record.id}
        header={header}
        extra={
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 4 }}>
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} />
            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={handleDelete} />
          </div>
        }
      >
        <div className="docking-card-body">
          <DetailRow label="对接时间">{record.dockedAt}</DetailRow>
          <DetailRow label="项目成熟度">
            {record.maturity ? `${'★'.repeat(record.maturity)}${'☆'.repeat(5 - record.maturity)}` : '—'}
          </DetailRow>
          <DetailRow label="对接人员（我方）">
            {record.ourParticipants.length > 0
              ? record.ourParticipants.map(participantText).join('、')
              : '—'}
          </DetailRow>
          <DetailRow label="对接单位">{record.counterpartyOrg || '—'}</DetailRow>
          <DetailRow label="对接单位参与人员">
            {record.counterpartyParticipants.length > 0
              ? record.counterpartyParticipants.map(participantText).join('、')
              : '—'}
          </DetailRow>
          <DetailRow label="对接情况">
            {record.summary ? (
              <span className="dash-detail-text-block">{record.summary}</span>
            ) : '—'}
          </DetailRow>
          {record.blockers && (
            <DetailRow label="当前工作卡点">
              <div>
                <span className="dash-detail-text-block">{record.blockers}</span>
                <div style={{ marginTop: 4, fontSize: 12 }}>
                  {record.blockerResolved ? (
                    <Tag color="success">已解决（{record.blockerResolvedAt || '—'}）</Tag>
                  ) : (
                    <Tag color="warning">未解决</Tag>
                  )}
                </div>
              </div>
            </DetailRow>
          )}
          {record.followUpPlan && (
            <DetailRow label="后续工作安排">
              <span className="dash-detail-text-block">{record.followUpPlan}</span>
            </DetailRow>
          )}
          {record.memos.length > 0 && (
            <DetailRow label="备忘录">
              <ul className="dash-attachment-list">
                {record.memos.map((f) => {
                  const att = toPreviewAttachment(f);
                  return (
                    <li key={f.id}>
                      {att ? (
                        <button
                          type="button"
                          className="dash-attachment-link"
                          onClick={() => setPreview(att)}
                        >
                          <FileWordOutlined style={{ marginRight: 4 }} />
                          {f.fileName}
                        </button>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.45)' }}>{f.fileName}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </DetailRow>
          )}
          {record.minutes.length > 0 && (
            <DetailRow label="会议纪要">
              <ul className="dash-attachment-list">
                {record.minutes.map((f) => {
                  const att = toPreviewAttachment(f);
                  return (
                    <li key={f.id}>
                      {att ? (
                        <button
                          type="button"
                          className="dash-attachment-link"
                          onClick={() => setPreview(att)}
                        >
                          <FileWordOutlined style={{ marginRight: 4 }} />
                          {f.fileName}
                        </button>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.45)' }}>{f.fileName}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </DetailRow>
          )}
          {record.taskAssignments.length > 0 && (
            <DetailRow label="工作任务分工">
              <TaskAssignmentEditor value={record.taskAssignments} onChange={() => {}} readOnly />
            </DetailRow>
          )}
        </div>
      </Panel>
      <AttachmentPreviewModal
        open={preview !== null}
        attachment={preview}
        onClose={() => setPreview(null)}
      />
    </Collapse>
  );
}
